/**
 * Admin Locked Wallet Management (v4.7.42)
 *
 * View, search (by wallet address, username, email, or user ID), change-address, and
 * deactivate user locked wallets.
 *
 * v4.7.19: Both "Change Address" and "Deactivate Wallet" actions are gated by a unified
 * compliance modal that REQUIRES (a) a reason of at least 10 characters and (b) a "Selfie
 * holding ID" image upload. The selfie is persisted server-side to the `proof` storage
 * bucket and its public URL is recorded in BOTH locked_wallet_audit_log.metadata AND a
 * fresh admin_audit_logs row, so every wallet change is permanently linked to a verified
 * identity proof.
 *
 * v4.7.42: Optional `initialUserId` prop — when supplied, the component auto-loads that
 * user's wallets on mount (used by the Master Audit dropdown's Johan-only quick-access).
 */

import React, { useState, useRef, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const ALLOWED_SELFIE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SELFIE_BYTES = 10 * 1024 * 1024;
const MIN_REASON_CHARS = 10;

interface LockedWalletData {
  id: string;
  user_id: number;
  wallet_address: string;
  network: string;
  wallet_type?: string;
  registered_at: string;
  registered_by: string;
  is_active: boolean;
  updated_at: string | null;
  updated_by: number | null;
  user?: { id: number; username: string; email: string; full_name: string };
}

interface AuditLogEntry {
  id: string;
  user_id: number;
  admin_id: number;
  action: string;
  network: string;
  old_wallet_address: string | null;
  new_wallet_address: string | null;
  reason: string | null;
  created_at: string;
  admin?: { username: string; email: string };
}

// v4.7.31: 'delete' permanently removes a DEACTIVATED wallet row so the address slot is freed.
type ComplianceMode = 'edit' | 'remove' | 'delete';

interface LockedWalletAdminProps {
  initialUserId?: number;
}

export const LockedWalletAdmin: React.FC<LockedWalletAdminProps> = ({ initialUserId }) => {
  const [searchQuery, setSearchQuery] = useState(initialUserId ? String(initialUserId) : '');
  const [wallets, setWallets] = useState<LockedWalletData[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAudit, setShowAudit] = useState(false);

  // v4.7.19: Unified compliance modal state — covers both Change Address and Deactivate flows.
  // The mode determines which extra fields render (newAddress for 'edit', spoken-to-user
  // checkbox for 'remove'); reason + selfie are mandatory in both modes.
  const [complianceModal, setComplianceModal] = useState<{ mode: ComplianceMode; wallet: LockedWalletData } | null>(null);
  const [newAddress, setNewAddress] = useState('');
  const [reason, setReason] = useState('');
  const [spokenConfirmed, setSpokenConfirmed] = useState(false);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AJAX search debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const callAdminAPI = async (operation: string, data: any) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/locked-wallets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({ operation, data })
    });
    return res.json();
  };

  const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read selfie file'));
    reader.readAsDataURL(file);
  });

  const performSearch = async (query?: string) => {
    const term = query ?? searchQuery;
    if (!term.trim()) return;
    setLoading(true); setError(null);
    try {
      // If it looks like a numeric user ID, load directly
      if (/^\d+$/.test(term.trim())) {
        const result = await callAdminAPI('get_user_wallets', { userId: parseInt(term.trim()) });
        if (!result.success) throw new Error(result.error);
        setWallets(result.wallets || []);
        const auditResult = await callAdminAPI('get_audit_log', { userId: parseInt(term.trim()) });
        setAuditLogs(auditResult.success ? auditResult.logs || [] : []);
      } else {
        // Search by wallet address or username
        const result = await callAdminAPI('search_wallets', { searchTerm: term.trim() });
        if (!result.success) throw new Error(result.error);
        setWallets(result.wallets || []);
        setAuditLogs([]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Real-time AJAX search on input change (debounced)
  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => performSearch(value), 500);
    }
  };

  // v4.7.42: when embedded with a target user pre-selected, auto-load their wallets once.
  const initialUserLoadedRef = useRef(false);
  useEffect(() => {
    if (typeof initialUserId === 'number' && !initialUserLoadedRef.current) {
      initialUserLoadedRef.current = true;
      performSearch(String(initialUserId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserId]);

  // v4.7.19: Open the unified compliance modal in either 'edit' or 'remove' mode.
  // Resets ALL transient state so a previous attempt can never leak across actions.
  const openComplianceModal = (mode: ComplianceMode, wallet: LockedWalletData) => {
    setComplianceModal({ mode, wallet });
    setNewAddress(mode === 'edit' ? wallet.wallet_address : '');
    setReason('');
    setSpokenConfirmed(false);
    setSelfieFile(null);
    setSelfiePreview(null);
    setError(null);
  };

  const closeComplianceModal = () => {
    setComplianceModal(null);
    setNewAddress('');
    setReason('');
    setSpokenConfirmed(false);
    setSelfieFile(null);
    setSelfiePreview(null);
  };

  const handleSelfieChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_SELFIE_MIME.includes(f.type)) {
      setError('Selfie must be JPEG, PNG, or WEBP.');
      return;
    }
    if (f.size > MAX_SELFIE_BYTES) {
      setError('Selfie file too large (max 10MB).');
      return;
    }
    setError(null);
    setSelfieFile(f);
    const reader = new FileReader();
    reader.onload = () => setSelfiePreview(reader.result as string);
    reader.readAsDataURL(f);
  };

  // v4.7.19: Single submission path for both Change Address and Deactivate Wallet.
  // Validates compliance prerequisites (reason length + selfie + mode-specific extras),
  // base64-encodes the selfie, dispatches to the correct admin operation, and refreshes
  // the wallet list on success. Errors surface inline so the modal stays open for retry.
  const handleComplianceSubmit = async () => {
    if (!complianceModal) return;
    const { mode, wallet } = complianceModal;
    if (reason.trim().length < MIN_REASON_CHARS) {
      setError(`Reason must be at least ${MIN_REASON_CHARS} characters.`);
      return;
    }
    if (!selfieFile) {
      setError('A "Selfie holding ID" photo is required for compliance.');
      return;
    }
    if (mode === 'edit') {
      if (!newAddress.trim()) { setError('New wallet address is required.'); return; }
      if (newAddress.trim().toLowerCase() === wallet.wallet_address.toLowerCase()) {
        setError('New address is identical to the current address.');
        return;
      }
    }
    // v4.7.31: 'remove' and 'delete' both require the spoken-to-user confirmation.
    if ((mode === 'remove' || mode === 'delete') && !spokenConfirmed) {
      setError('You must confirm that you have spoken to the user.');
      return;
    }
    // v4.7.31: 'delete' is only valid against an already-deactivated wallet.
    if (mode === 'delete' && wallet.is_active) {
      setError('Cannot delete an active wallet. Deactivate it first, then delete.');
      return;
    }

    setLoading(true); setError(null);
    try {
      const dataUrl = await fileToDataUrl(selfieFile);
      const compliancePayload = {
        selfie_file_base64: dataUrl,
        selfie_file_name: selfieFile.name,
        selfie_content_type: selfieFile.type
      };

      let result;
      if (mode === 'edit') {
        result = await callAdminAPI('update_wallet', {
          walletId: wallet.id,
          newWalletAddress: newAddress.trim(),
          reason: reason.trim(),
          personal_wallet_confirmed: true,
          ...compliancePayload
        });
      } else if (mode === 'delete') {
        // v4.7.31: hard-delete the deactivated wallet row.
        result = await callAdminAPI('delete_wallet', {
          walletId: wallet.id,
          reason: reason.trim(),
          confirmation_spoken_to_user: true,
          ...compliancePayload
        });
      } else {
        result = await callAdminAPI('deactivate_wallet', {
          walletId: wallet.id,
          reason: reason.trim(),
          confirmation_spoken_to_user: true,
          ...compliancePayload
        });
      }
      if (!result.success) throw new Error(result.error || 'Operation failed');
      setSuccess(
        mode === 'edit' ? 'Wallet address updated successfully'
        : mode === 'delete' ? 'Wallet record permanently deleted'
        : 'Wallet removed successfully'
      );
      closeComplianceModal();
      await performSearch();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">🔒 Locked Wallet Management</h2>
      </div>

      {/* Search — by wallet address, username, or user ID */}
      <div className="flex gap-3 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-gray-400 text-sm mb-1">Search by Wallet Address, Username, or User ID</label>
          <input type="text" value={searchQuery} onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && performSearch()}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="0x..., username, email, or user ID" />
          <p className="text-gray-500 text-xs mt-1">Results appear automatically after 3+ characters</p>
        </div>
        <button onClick={() => performSearch()} disabled={loading || !searchQuery.trim()}
          className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-white px-4 py-2 rounded transition-colors">
          {loading ? 'Loading...' : '🔍 Search'}
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-500/30 rounded p-3 text-red-300 text-sm">{error}</div>}
      {success && <div className="bg-green-900/30 border border-green-500/30 rounded p-3 text-green-300 text-sm">{success}</div>}

      {/* Wallets list */}
      {wallets.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-white font-medium">Found {wallets.length} wallet(s)</h3>
          {wallets.map(w => (
            <div key={w.id} className={`rounded-lg p-4 border ${w.is_active ? 'bg-gray-800 border-gray-700' : 'bg-gray-900 border-gray-800 opacity-60'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-400 font-bold">{w.network}</span>
                  {w.wallet_type && <span className="text-xs px-2 py-0.5 rounded bg-blue-900/50 text-blue-300">{w.wallet_type}</span>}
                </div>
                <span className={`text-xs px-2 py-1 rounded ${w.is_active ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
                  {w.is_active ? '🔒 Active' : '❌ Deactivated'}
                </span>
              </div>
              <p className="text-white font-mono text-sm break-all mb-2">{w.wallet_address}</p>
              {w.user && <p className="text-blue-300 text-xs mb-1">👤 {w.user.username} ({w.user.email}) — User #{w.user_id}</p>}
              <p className="text-gray-500 text-xs">Registered: {new Date(w.registered_at).toLocaleString()} by {w.registered_by}</p>
              {w.updated_by && <p className="text-gray-500 text-xs">Last updated by admin #{w.updated_by} at {w.updated_at ? new Date(w.updated_at).toLocaleString() : 'N/A'}</p>}

              {/* Admin Actions — v4.7.19: actions route through the unified compliance modal.
                  v4.7.31: deactivated wallets can be permanently DELETED so the address slot
                  is freed and the user (or anyone) can register the same address again. */}
              {w.is_active ? (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <div className="flex gap-2 flex-wrap items-end">
                    <button onClick={() => openComplianceModal('edit', w)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded">✏️ Change Address</button>
                    <button onClick={() => openComplianceModal('remove', w)}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded">🗑️ Remove Wallet</button>
                    <span className="text-gray-500 text-xs ml-2">⚠️ Both actions require Selfie holding ID + reason</span>
                  </div>
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <div className="flex gap-2 flex-wrap items-end">
                    <button onClick={() => openComplianceModal('delete', w)}
                      className="bg-red-700 hover:bg-red-800 text-white text-xs px-3 py-1 rounded">💣 Permanently Delete</button>
                    <span className="text-gray-500 text-xs ml-2">⚠️ Hard-deletes the deactivated record · requires Selfie holding ID + reason</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : searchQuery && !loading ? (
        <p className="text-gray-400 text-sm">No locked wallets found for this search.</p>
      ) : null}

      {/* Audit Log */}
      {auditLogs.length > 0 && (
        <div className="mt-6">
          <button onClick={() => setShowAudit(!showAudit)} className="text-yellow-400 text-sm hover:text-yellow-300">
            {showAudit ? '▼' : '▶'} Audit Log ({auditLogs.length} entries)
          </button>
          {showAudit && (
            <div className="mt-2 space-y-2">
              {auditLogs.map(log => (
                <div key={log.id} className="bg-gray-800/50 border border-gray-700 rounded p-3 text-sm">
                  <div className="flex justify-between items-start">
                    <span className="text-yellow-400 font-medium">{log.action.toUpperCase()}</span>
                    <span className="text-gray-500 text-xs">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-300 text-xs mt-1">Network: {log.network}</p>
                  {log.old_wallet_address && <p className="text-red-400 text-xs font-mono">Old: {log.old_wallet_address}</p>}
                  {log.new_wallet_address && <p className="text-green-400 text-xs font-mono">New: {log.new_wallet_address}</p>}
                  {log.reason && <p className="text-gray-400 text-xs mt-1">Reason: {log.reason}</p>}
                  <p className="text-gray-500 text-xs">Admin: {log.admin?.username || `#${log.admin_id}`}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* v4.7.19: Unified Compliance Modal — Change Address, Deactivate Wallet, OR (v4.7.31) Permanently Delete.
          All actions REQUIRE a Selfie holding ID + reason (min 10 chars). */}
      {complianceModal && (() => {
        const { mode, wallet } = complianceModal;
        const isEdit = mode === 'edit';
        const isDelete = mode === 'delete';
        const verbNoun = isEdit ? 'Change' : isDelete ? 'Deletion' : 'Removal';
        const verbProgressive = isEdit ? 'changing' : isDelete ? 'permanently deleting' : 'removing';
        const reasonOk = reason.trim().length >= MIN_REASON_CHARS;
        const addressOk = !isEdit || newAddress.trim().length > 0;
        const spokenOk = isEdit || spokenConfirmed;
        const canSubmit = reasonOk && addressOk && spokenOk && !!selfieFile && !loading;
        return (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gray-800 border border-gray-600 rounded-xl max-w-lg w-full p-6 shadow-2xl my-8">
              <h3 className={`text-lg font-bold mb-4 ${isEdit ? 'text-blue-400' : 'text-red-400'}`}>
                {isEdit ? '✏️ Change Wallet Address'
                  : isDelete ? '💣 Permanently Delete Wallet Record'
                  : '⚠️ Remove Wallet Confirmation'}
              </h3>

              <div className="bg-gray-700/50 rounded p-3 mb-4">
                <p className="text-gray-300 text-xs"><strong>User:</strong> {wallet.user?.username || `#${wallet.user_id}`} ({wallet.user?.email})</p>
                <p className="text-gray-300 text-xs font-mono mt-1"><strong>Current Wallet:</strong> {wallet.wallet_address}</p>
                <p className="text-gray-300 text-xs mt-1"><strong>Network:</strong> {wallet.network}{wallet.wallet_type ? ` · ${wallet.wallet_type}` : ''}</p>
                {isDelete && (
                  <p className="text-red-300 text-xs mt-2">
                    ⚠️ This will <strong>permanently remove</strong> the wallet record. The audit history is preserved, and the address slot is freed so it can be registered again.
                  </p>
                )}
              </div>

              {/* Edit-only: new address */}
              {isEdit && (
                <div className="mb-4">
                  <label className="block text-gray-300 text-sm mb-1">New Wallet Address <span className="text-red-400">*</span></label>
                  <input type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono"
                    placeholder="0x... or T..." />
                </div>
              )}

              {/* Remove/Delete: spoken-to-user confirmation */}
              {!isEdit && (
                <label className="flex items-start gap-2 mb-4 cursor-pointer">
                  <input type="checkbox" checked={spokenConfirmed} onChange={(e) => setSpokenConfirmed(e.target.checked)}
                    className="mt-1 accent-red-500" />
                  <span className="text-yellow-200 text-sm">
                    I confirm that I have <strong>personally spoken to the user</strong> and verified their identity before {isDelete ? 'permanently deleting' : 'removing'} this wallet.
                  </span>
                </label>
              )}

              {/* Reason — mandatory in all modes */}
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">
                  Reason for {verbNoun} <span className="text-red-400">*</span>{' '}
                  <span className="text-gray-500 text-xs">(min {MIN_REASON_CHARS} characters)</span>
                </label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none"
                  placeholder={`Provide a detailed reason for ${verbProgressive} this wallet...`} />
                <p className={`text-xs mt-1 ${reasonOk ? 'text-green-400' : 'text-gray-500'}`}>
                  {reason.trim().length}/{MIN_REASON_CHARS} characters minimum
                </p>
              </div>

              {/* Mandatory Selfie holding ID — v4.7.19 compliance gate */}
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">
                  Selfie holding ID <span className="text-red-400">*</span>{' '}
                  <span className="text-gray-500 text-xs">(JPEG/PNG/WEBP, max 10MB)</span>
                </label>
                <input type="file" ref={fileInputRef} onChange={handleSelfieChange}
                  accept="image/jpeg,image/png,image/webp" className="hidden" />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className={`px-3 py-2 rounded border text-xs ${selfieFile ? 'bg-green-900/40 border-green-600 text-green-200' : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'}`}>
                  {selfieFile ? `✓ ${selfieFile.name}` : '📷 Upload Selfie holding ID'}
                </button>
                {selfiePreview && (
                  <div className="mt-2">
                    <img src={selfiePreview} alt="Selfie preview" className="max-h-40 rounded border border-gray-600" />
                  </div>
                )}
                <p className="text-yellow-300/80 text-xs mt-2">
                  ⚠️ This image is permanently linked to the audit record and the action cannot proceed without it.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={handleComplianceSubmit} disabled={!canSubmit}
                  className={`flex-1 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 rounded font-medium transition-colors ${isEdit ? 'bg-blue-600 hover:bg-blue-700' : isDelete ? 'bg-red-700 hover:bg-red-800' : 'bg-red-600 hover:bg-red-700'}`}>
                  {loading ? 'Submitting...' : isEdit ? '✏️ Confirm Change' : isDelete ? '💣 Permanently Delete' : '🗑️ Confirm Remove'}
                </button>
                <button onClick={closeComplianceModal} disabled={loading}
                  className="flex-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white py-2 rounded transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LockedWalletAdmin;
